// =============================================================================
// KARTA POBIERANIA JEDNEJ RODZINY URZĄDZEŃ.
//
// Do 2026-08-25 ten sam układ (nagłówek z wersją → przyciski platform → kod QR
// → sumy kontrolne) stał w `DevicesPage.tsx` TRZY RAZY, przepisany ręcznie,
// w trzech różnych kolorach akcentu i z trzema różnymi komunikatami braku.
// Czwarta rodzina — serwer wydruku — byłaby czwartą kopią, więc układ jest
// tutaj raz, a różnice wchodzą parametrem.
//
// Kod QR pokazujemy TYLKO dla pliku oznaczonego w katalogu jako `qr`. Aparat
// telefonu ma sens przy APK; instalator peceta, paczkę Raspberry Pi i pakiet
// serwera wydruku przenosi się pendrivem albo `curl`-em, a kod QR obok nich
// obiecywałby drogę, której nie ma.
// =============================================================================

import { Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import type { ReactNode } from 'react';

import type { PozycjaWydania, WydanieZRejestru } from './katalogWydan';

export interface PozycjaDoPobrania {
  pozycja: PozycjaWydania;
  wydanie: WydanieZRejestru;
  url: string;
}

interface Props {
  ikona: ReactNode;
  tytul: string;
  /** Wydanie, z którego bierzemy numer wersji w nagłówku (platforma wiodąca). */
  wersja?: WydanieZRejestru;
  pozycje: PozycjaDoPobrania[];
  /** Adres pliku, do którego prowadzi kod QR; `null` = bez kodu. */
  urlQr: string | null;
  tytulQr: string;
  naglowekInstrukcji: string;
  komunikatBrak: string;
  children?: ReactNode;
}

export function KartaAplikacji({
  ikona,
  tytul,
  wersja,
  pozycje,
  urlQr,
  tytulQr,
  naglowekInstrukcji,
  komunikatBrak,
  children,
}: Props) {
  return (
    <div className="mb-6 card p-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-ids-md bg-aurora-brand/15 flex items-center justify-center text-aurora-brand">
            {ikona}
          </div>
          <div>
            <h2 className="text-lg font-bold text-aurora-text">{tytul}</h2>
            {wersja?.version_name ? (
              <p className="text-sm text-aurora-muted">
                Najnowsza wersja:{' '}
                <span className="text-aurora-text font-medium">{wersja.version_name}</span> (kod{' '}
                {wersja.version_code})
              </p>
            ) : (
              <p className="text-sm text-aurora-muted">Brak opublikowanego wydania</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {pozycje.map(({ pozycja, url }) => (
            <a
              key={pozycja.klucz}
              href={url}
              download
              title={pozycja.opis}
              className={`btn btn--sm ${pozycja.glowna ? 'btn--primary' : 'btn--tonal'}`}
            >
              <Download className="w-4 h-4" />
              {pozycja.etykieta}
            </a>
          ))}
          {pozycje.length === 0 && (
            <span className="text-xs text-aurora-subtle">{komunikatBrak}</span>
          )}
        </div>
      </div>

      {pozycje.length > 0 && (
        <div className="mt-4 pt-4 border-t border-aurora-border flex items-start gap-4 flex-wrap">
          {/* Biała płytka pod kodem to nie ozdobnik: kontrast bezwzględny jest
              warunkiem zeskanowania, więc kod QR nie może odwracać się z trybem. */}
          {urlQr && (
            <div className="rounded-ids-lg bg-white p-4 flex-shrink-0">
              <QRCodeSVG
                value={urlQr}
                size={148}
                level="M"
                marginSize={0}
                fgColor="#0f172a"
                bgColor="#ffffff"
                title={tytulQr}
              />
            </div>
          )}
          <div className="flex-1 min-w-[220px]">
            <h3 className="text-sm font-semibold text-aurora-text">{naglowekInstrukcji}</h3>
            {children}
            {/* Suma kontrolna PER PLIK, a nie jedna dla rodziny: to są różne
                pliki i suma ma sens tylko przy tym, który się naprawdę pobrało. */}
            <div className="mt-3 space-y-1">
              {pozycje
                .filter(({ wydanie }) => wydanie.sha256)
                .map(({ pozycja, wydanie }) => (
                  <p key={pozycja.klucz} className="text-xs text-aurora-subtle mono break-all">
                    <span className="text-aurora-muted">{pozycja.platform}</span> SHA-256:{' '}
                    {wydanie.sha256}
                  </p>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
